import TextArea from "antd/es/input/TextArea";
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useReactFlow, XYPosition } from "reactflow";
import { InstanceMapContext } from "../../canvas-view/canvas-flow";
import { getAllReferenceCopies } from "../../../nodes/topic-node/topic-node.helper";
import { Instance, InstanceState } from "../../../triggers/semantic-dive/semantic-dive";

import defaultLayerImage from "../../../../assets/images/new-layer-default.png";

import { ReactComponent as DeleteIcon } from "../../../../assets/icons/delete.svg";
import './layer.scss';
import { generateSubtopics } from "../../../triggers/hierarchy-view/hierarchy-view.helper";
import { getChildHierarchyNodes } from "../../../nodes/hierarchy-node/hierarchy-node.helper";
import React from "react";
import { Popconfirm } from "antd";

interface LayerProps {
  hasLoadedLayers: boolean,
  layerTopicId: string,
  layerPosition: XYPosition,
  currentTopicId: string,
  viewportData: {
    minLeft: number,
    maxLeft: number,
    top: number,
    maxLevel: number,
  }
  hideLayerView: Function,
  addNewLayer: Function,
  deleteLayer: Function,
  recommendedSubtopics: { [x: string]: string[]; },
  setRecommendedSubtopics: Dispatch<SetStateAction<{ [x: string]: string[]; }>>
}

/**
 * Creates the layer HTMLElement
 * props:
 *  instance
 *  currentTopicId
 */
const Layer = ({
  hasLoadedLayers,
  layerTopicId,
  layerPosition,
  currentTopicId,
  viewportData,
  hideLayerView,
  addNewLayer,
  deleteLayer,
  recommendedSubtopics,
  setRecommendedSubtopics,
}: LayerProps) => {
  const {instanceMap, setInstanceMap} = useContext(InstanceMapContext);
  const reactFlowInstance = useReactFlow();

  const instance = instanceMap[layerTopicId];

  const [topicName, setTopicName] = useState(instanceMap[layerTopicId]?.topicNode.data.state.topic || 'New Topic');
  const [isEditing, setIsEditing] = useState(false);

  // Start animating when recommended topic is clicked
  const [addingRecommendation, setAddingRecommendation] = useState(false);
  // const [recommendedSubtopics, setRecommendedSubtopics] = useState<string[]>([]);
  const [generatingTopic, setGeneratingTopic] = useState(false);

  const generateNewSubtopics = useCallback(() => {
    if (!recommendedSubtopics[layerTopicId]) {
      recommendedSubtopics[layerTopicId] = [];
      setRecommendedSubtopics(recommendedSubtopics);
    }
    if (recommendedSubtopics[layerTopicId].length <= 0) {
      setGeneratingTopic(true);
      const childTopicNames = getChildHierarchyNodes(layerTopicId, instanceMap)
        .map(instance => instance.topicNode.data.state.topic ?? '')
        .filter((topic): topic is string => !!topic);
  
      return generateSubtopics(instance.topicNode, childTopicNames).then(subtopics => {
        const firstTopic = subtopics.pop();
        recommendedSubtopics[layerTopicId].push(...subtopics);
        setRecommendedSubtopics(recommendedSubtopics);
        setGeneratingTopic(false);
        return firstTopic;
      });
    } else {
      return Promise.resolve(recommendedSubtopics[layerTopicId].pop());
    }
  }, [instanceMap, layerTopicId, recommendedSubtopics]);

  const addSubLayer = useCallback(async (recommendTopics: boolean, layerId: string) => {
    let topicName = `New Topic`;

    if (recommendTopics) {
      if (recommendedSubtopics[layerId] && recommendedSubtopics[layerId].length > 0) {
        topicName = recommendedSubtopics[layerId].pop() ?? topicName;
        setRecommendedSubtopics(recommendedSubtopics);
      } else {
        topicName = (await generateNewSubtopics()) ?? 'New Topic';
      }
    }

    addNewLayer(false, layerId, topicName);
  }, [instanceMap, layerTopicId, recommendedSubtopics])

  const handleChange = (event: any) => {
    setTopicName(event.target.value);
  }

  const beginEditing = useCallback(() => {
    setIsEditing(true);
    setTopicName(instanceMap[layerTopicId].topicNode.data.state.topic ?? topicName);
    setTimeout(() => {
      document.getElementById('topic-textarea')?.focus();
    }, 100);
  }, [isEditing, instanceMap, topicName]);

  const endEditing = useCallback(() => {
    if (topicName.trim()) {
      document.getElementById('topic-textarea')?.blur();
      setIsEditing(false);
      updateTopicName(layerTopicId, topicName);
    }
  }, [isEditing, topicName, layerTopicId]);

  /**
   * Update the name of the topic across all topic nodes and its copies
   */
  const updateTopicName = useCallback((topicId: string, topicName: string) => {
    const references = getAllReferenceCopies(instanceMap[topicId].topicNode, instanceMap);
    console.log('ref', references);
    // if it's a parent node of the child, 
    references.forEach(topicNode => {
      topicNode.data.state.topic = topicName;
    });

    instanceMap[topicId].topicNode.data.state.topic = topicName;
    setInstanceMap(instanceMap);
  }, [reactFlowInstance, instanceMap]);

  const handleEnterPress = (event: any) => {
    if(event.key === 'Enter'){
      endEditing();
    }
  }

  const isLayerRoot = (instance: Instance) => {
    return instance.topicNode.id === 'topic-root';
  }

  return (
    <div
      key={`layer-key-${instance.topicNode.id}`}
    >
      <div
        id={`layer-${instance.topicNode.id}`}
        className={
          `layer 
          ${currentTopicId === instance.topicNode.id ?
            ' current-layer' : ''
          }
          ${currentTopicId === instance.topicNode.id && !hasLoadedLayers ?
            ' minimize-layer' : ''
          }
          `
        }
        style={{
          visibility: `${isLayerRoot(instance) ? 'hidden' : 'visible'}`,
          left: `${layerPosition.x}px`,
          top: `${layerPosition.y}px`,
          scale: `${0.3/Math.pow(instance.level+1, 1/3)}`,
          zIndex: viewportData.maxLevel-instance.level+1
        }}
        onMouseEnter={() => {
          console.log(layerTopicId, instanceMap[layerTopicId])
        }}
        onClick={(event: any) => {
          if (event.target.nodeName === 'IMG') {
            (event.target.parentElement as HTMLElement).classList.add('clicked-layer');
            event.target.parentElement.style.translate = `${window.scrollX-(viewportData.maxLeft-viewportData.minLeft)/2}px ${window.scrollY}px`;
            setTimeout(() => {
              hideLayerView(instance.topicNode.id);
            }, 500);
          }
        }}
      >
        <img src={instance.canvasImageURL || defaultLayerImage} width={window.innerWidth} height={window.innerHeight} />
        <Popconfirm
          className="delete-confirm"
          title="Delete layer"
          description="Are you sure to delete this task? All layers below this one will get deleted too."
          okText="Delete"
          cancelText="Cancel"
          onConfirm={() => deleteLayer(layerTopicId)}
        >
          <div
            className="delete-layer"
            onClick={(e) => e.preventDefault()}
          >
            <DeleteIcon />
          </div>
        </Popconfirm>
      </div>
      <div
        className='add-sublayer'
        style={{
          position: 'absolute',
          top: `${layerPosition.y + 50}px`,
          left: `${layerPosition.x}px`,
          scale: `${0.3/Math.pow(instance.level < 0 ? 1 : instance.level+1, 1/3)}`,
          zIndex: viewportData.maxLevel-instance.level
        }}>
        <div
          className="add-sublayer-choice"
          onClick={() => addSubLayer(false, instance.topicNode.id)}
        >
          <a>Custom Subtopic</a>
        </div>
        <div
          className="add-sublayer-choice"
          onClick={() => addSubLayer(true, instance.topicNode.id)}
          >
          <a>Generated Subtopic</a>
        </div>
      </div>
      <div
        className='layer-label'
        onDoubleClick={beginEditing}
        style={{
          position: 'absolute',
          // These magic numbers are for positioning the label to the bottom left of each layer
          top: `${window.innerHeight/2 + layerPosition.y + (window.innerHeight/10)/Math.pow(instance.level+1, 1/3)}px`,
          left: `${window.innerWidth/2 + layerPosition.x - (window.innerWidth/5.4)/Math.pow(instance.level+1, 1/3)}px`,
          zIndex: viewportData.maxLevel-instance.level+1
        }}
      >
        {
          isEditing ?
          <TextArea
            id='topic-textarea'
            value={topicName}
            onChange={handleChange} 
            onFocus={(event) => event.target.select()}
            onBlur={endEditing}
            onKeyDown={handleEnterPress}
            autoSize
            style={{
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
            }}
          /> :
          <>{instanceMap[layerTopicId]?.topicNode.data.state.topic} {generatingTopic ? '• Generating subtopic...' : ''}</>
        }
      </div>
    </div>
  )
};

export default React.memo(Layer);